import api, { apiError, brl, fileUrl, rows } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { RotateCcw, FileDown } from "lucide-react";
import usePaginatedList from "@/hooks/usePaginatedList";
import Pagination from "@/components/panel/Pagination";
import { TableSkeleton, ErrorState } from "@/components/panel/ui";

const STATUS_LABEL = {
  pendente: ["Pendente", "bg-slate-100 text-slate-600"],
  pago: ["Pago", "bg-blue-50 text-blue-700"],
  separacao: ["Em separação", "bg-amber-50 text-amber-700"],
  enviado: ["Enviado", "bg-violet-50 text-violet-700"],
  entregue: ["Entregue", "bg-emerald-50 text-emerald-700"],
  cancelado: ["Cancelado", "bg-red-50 text-red-600"],
};

export default function MyOrders() {
  // Paginação no servidor: o histórico antes vinha cortado nos 100 últimos pedidos.
  // Aliases mantêm o JSX abaixo intacto.
  const {
    items: orders, total, page, lastPage, setPage,
    isLoading: loading, isError: error, refetch: retry,
  } = usePaginatedList("/orders/my");
  const { addItem, setOpen } = useCart();

  const rebuy = async (order) => {
    try {
      for (const it of order.items) {
        const res = await api.get("/products", { params: { search: it.product_name } });
        const p = rows(res).find((x) => x.id === it.product_id);
        if (!p) continue;
        const v = p.variants.find((x) => x.id === it.variant_id);
        if (v && v.stock > 0) addItem(p, v, it.qty);
      }
      setOpen(true);
      toast.success("Itens adicionados ao carrinho");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <TableSkeleton rows={3} cols={4} />;
  if (error) return <ErrorState onRetry={retry} message="Não foi possível carregar seus pedidos." />;
  if (orders.length === 0) return <p className="text-slate-500" data-testid="orders-empty-msg">Você ainda não fez nenhum pedido.</p>;

  return (
    <div className="space-y-4" data-testid="orders-list">
      {orders.map((o) => {
        const [label, cls] = STATUS_LABEL[o.status] || STATUS_LABEL.pendente;
        return (
          <div key={o.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid="order-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className="font-mono font-semibold text-slate-900" data-testid="order-number">{o.number}</span>
                <span className="text-xs text-slate-400 ml-3">{new Date(o.created_at).toLocaleDateString("pt-BR")}</span>
              </div>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`} data-testid="order-status">{label}</span>
            </div>
            <div className="mt-3 text-sm text-slate-600 space-y-1">
              {o.items.map((i, idx) => <p key={idx}>{i.qty}x {i.product_name} ({i.variant_label})</p>)}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between text-sm gap-2">
              <span className="text-slate-500">
                {o.shipping_method} {o.tracking_code && <>· Rastreio: <span className="font-mono text-brand-700" data-testid="order-tracking">{o.tracking_code}</span></>}
              </span>
              <div className="flex items-center gap-3">
                {o.invoice_id && (
                  <a href={fileUrl(`/invoices/${o.invoice_id}/pdf`)} target="_blank" rel="noreferrer" data-testid="order-invoice-btn"
                    className="text-xs font-semibold text-slate-600 hover:text-brand-700 inline-flex items-center gap-1"><FileDown size={13} /> NF-e</a>
                )}
                <button onClick={() => rebuy(o)} data-testid="order-rebuy-btn"
                  className="text-xs font-semibold text-brand-700 hover:bg-brand-50 border border-brand-200 rounded-lg px-3 py-1.5 inline-flex items-center gap-1"><RotateCcw size={13} /> Comprar novamente</button>
                <span className="font-mono font-semibold text-slate-900">{brl(o.total)}</span>
              </div>
            </div>
          </div>
        );
      })}
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="pedidos" />
    </div>
  );
}
